const GA_DEBUG_QUERY_PARAM = 'ga_debug';
const PURCHASE_TRACKING_PREFIX = 'ga4_purchase_';

const persistDebugFlag = (search = '') => {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(search || window.location.search || '');
  if (sp.get(GA_DEBUG_QUERY_PARAM) === '1') {
    try { sessionStorage.setItem('__ga_debug', '1'); } catch (_) { /* private mode */ }
  }
};

const shouldEnableDebugMode = (search = '') => {
  if (typeof window === 'undefined') return false;
  const searchParams = new URLSearchParams(search || window.location.search || '');
  if (searchParams.get(GA_DEBUG_QUERY_PARAM) === '1') return true;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return true;
  try { return sessionStorage.getItem('__ga_debug') === '1'; } catch (_) { return false; }
};

const getPageContext = (locationLike = {}) => {
  if (typeof window === 'undefined') {
    return {
      pageLocation: '',
      pagePath: '',
      search: '',
    };
  }

  const pathname = locationLike.pathname || window.location.pathname;
  const search = locationLike.search || window.location.search || '';
  const hash = locationLike.hash || window.location.hash || '';

  return {
    pageLocation: `${window.location.origin}${pathname}${search}${hash}`,
    pagePath: `${pathname}${search}`,
    search,
  };
};

export const initializeGa4 = (locationLike = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  const { search } = getPageContext(locationLike);
  persistDebugFlag(search);
};

export const trackGaPageView = (locationLike = {}, pageTitle = document.title) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  const { pageLocation, pagePath, search } = getPageContext(locationLike);
  persistDebugFlag(search);
  initializeGa4(locationLike);

  const eventPayload = {
    page_title: pageTitle,
    page_path: pagePath,
    page_location: pageLocation,
    ...(shouldEnableDebugMode(search) ? { debug_mode: true } : {}),
  };

  window.gtag('event', 'page_view', eventPayload);
};

export const trackBeginCheckout = ({
  value,
  currency = 'USD',
  items = [],
  purchaseType = 'ticket',
} = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  initializeGa4();
  const debugMode = shouldEnableDebugMode();
  window.gtag('event', 'begin_checkout', {
    currency,
    value: Number(value || 0),
    purchase_type: purchaseType,
    items: items.map((item, i) => ({
      item_id: `${item?.id || item?.ticket_type_id || i + 1}`,
      item_name: item?.name || item?.item_name || item?.ticket_type_name || 'Ticket',
      item_category: item?.category || item?.item_category || 'Ticket',
      item_variant: item?.variant || item?.item_variant || '',
      price: Number(item?.price || 0),
      quantity: Number(item?.quantity || 1),
    })),
    ...(debugMode ? { debug_mode: true } : {}),
  });
};

export const trackFormSubmit = (eventName, {
  formName,
  formLocation,
  registrationType,
} = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  initializeGa4();
  const debugMode = shouldEnableDebugMode();
  window.gtag('event', eventName, {
    form_name: formName,
    form_location: formLocation,
    ...(registrationType ? { registration_type: registrationType } : {}),
    ...(debugMode ? { debug_mode: true } : {}),
  });
};

export const trackGaPurchase = ({
  orderNumber,
  value,
  currency = 'USD',
  tax = 0,
  items = [],
  search = '',
  purchaseType = '',
  ticketType = '',
} = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function' || !orderNumber) return;

  const sessionStorageKey = `${PURCHASE_TRACKING_PREFIX}${orderNumber}`;
  if (window.sessionStorage?.getItem(sessionStorageKey)) return;

  initializeGa4();
  const debugMode = shouldEnableDebugMode(search);

  const purchasePayload = {
    transaction_id: orderNumber,
    value: Number(value || 0),
    currency,
    tax: Number(tax || 0),
    ...(purchaseType ? { purchase_type: purchaseType } : {}),
    ...(ticketType ? { ticket_type: ticketType } : {}),
    items: items.map((item, index) => ({
      item_id: `${item?.id || item?.ticket_type || item?.ticket_code || index + 1}`,
      item_name: item?.ticket_type_name || item?.name || item?.item_name || 'Ticket',
      item_category: item?.item_category || item?.ticket_type_name || 'Ticket',
      price: Number(
        item?.price ?? item?.unit_price ?? ((item?.unit_price_cents || item?.price_cents || 0) / 100)
      ),
      quantity: Number(item?.quantity || 1),
    })),
    ...(debugMode ? { debug_mode: true } : {}),
  };

  window.gtag('event', 'purchase', purchasePayload);

  window.sessionStorage?.setItem(sessionStorageKey, '1');
};
